import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    Switch,
    TextField,
    ToggleButton, ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import { useEffect, useMemo, useState } from 'react';
import ChipSelect from '../../tools/ChipSelect';
import { useAchievementColors } from '../hooks/useAchievementColors';


// Dummy data for teams
const dummyTeams = [
    {
        members: [{ user_id: 28, username: "Nacha" }, { user_id: 35, username: "Matt" }],
        team_id: 2,
        team_name: "Cloud Engineering"
    },
];

// Dummy data for users
const dummyUsers = [
    {
        avatar: "https://lh3.googleusercontent.com/a/ACg8ocJpMZLiA8PeULTonE2D_A_TpcGZenDSKsBjljqp38f4g1yw=s96-c",
        can_create_projects: false,
        email: "alvaro.romero-int@zippedi.com",
        first_name: "Romero",
        is_admin: false,
        label: "Álvaro",
        last_name: "Álvaro",
        value: 205
    }
];

const AchievementManagement = ({
    postAchievement,
    updateAchievement,
    deleteAchievement,
    getAllAchievements,
    uploadAchievementImage,
    allAchievements = [],
    isAllAchievementsLoading = false,
    isAchievementsTypesLoading = false,
    achievementsTypes = [],
    getUsers,
    getTeams,
    getAchievementsTypes,
    users = dummyUsers,
    isUsersLoading = false,
    teams = dummyTeams,
    isTeamsLoading = false,
    isUploadingAchievementImage,
    uploadAchievementImageSuccess,
    uploadAchievementImageError,
    uploadedAchievementImageData,
}) => {
    const [openDialog, setOpenDialog] = useState(false);
    const [editingAchievement, setEditingAchievement] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [achievementToDelete, setAchievementToDelete] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [achievementsPerPage, setAchievementsPerPage] = useState(6);
    const [isGridView, setIsGridView] = useState(true); // New state for view mode
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
    const [selectedTypes, setSelectedTypes] = useState([]);
    const getAchievementColors = useAchievementColors(achievementsTypes);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        locked_description: '',
        achievement_type: 'easy',
        group_id: '',
        max_actions_required: '',
        blob_path: '',
        bucket_name: '',
        due_time: '',
        team_id: '',
        user_id: '',
        is_hidden: true,
        is_shareable: true
    });
    const [actionType, setActionType] = useState(null); // 'create' | 'update' | 'delete'

    const [currentAchievements, setCurrentAchievements] = useState([]);
    const [totalPages, setTotalPages] = useState(0);

    const filteredAchievements = useMemo(() => {
        let achievements = [...allAchievements];

        if (searchTerm.trim()) {
            achievements = achievements.filter(achievement =>
                achievement.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (selectedTypes.length > 0) {
            achievements = achievements.filter(achievement =>
                selectedTypes.includes(achievement.achievement_type)
            );
        }

        achievements.sort((a, b) => {
            const typeComparison = a.achievement_type.localeCompare(b.achievement_type);
            if (typeComparison !== 0) {
                return sortOrder === 'asc' ? typeComparison : -typeComparison;
            }
            return sortOrder === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
        });

        return achievements;
    }, [allAchievements, searchTerm, sortOrder, selectedTypes]);

    useEffect(() => {
        const indexOfLastAchievement = currentPage * achievementsPerPage;
        const indexOfFirstAchievement = indexOfLastAchievement - achievementsPerPage;
        setCurrentAchievements(filteredAchievements.slice(indexOfFirstAchievement, indexOfLastAchievement));
        setTotalPages(Math.ceil(filteredAchievements.length / achievementsPerPage));
    }, [filteredAchievements, currentPage, achievementsPerPage]);

    useEffect(() => {
        if (!isUsersLoading && !users.length) {
            getUsers();
        }
        if (!isTeamsLoading && !teams.length) {
            getTeams();
        }
        if (!isAllAchievementsLoading) {
            getAllAchievements();
        }
        if (!isAchievementsTypesLoading && !achievementsTypes.length) {
            getAchievementsTypes();
        }
    }, [getAllAchievements, getUsers, getTeams, getAchievementsTypes]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, achievementsPerPage, selectedTypes]); // Add selectedTypes to dependency array

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
    };

    const handleAchievementsPerPageChange = (event) => {
        setAchievementsPerPage(Number(event.target.value));
        setCurrentPage(1); // Reset to first page when items per page changes
    };

    useEffect(() => {
        if (uploadAchievementImageSuccess && uploadedAchievementImageData) {
            setFormData(prev => ({
                ...prev,
                blob_path: uploadedAchievementImageData.blob_path,
                bucket_name: uploadedAchievementImageData.bucket_name
            }));
            setSelectedFile(null);
        }
    }, [uploadAchievementImageSuccess, uploadedAchievementImageData]);

    useEffect(() => {
        if (uploadAchievementImageError) {
        }
    }, [uploadAchievementImageError]);

    // useEffect para cerrar diálogos y recargar achievements cuando la acción termina
    useEffect(() => {
        if (!isAllAchievementsLoading && actionType) {
            getAllAchievements();
            if (actionType === 'delete') {
                setDeleteDialogOpen(false);
                setAchievementToDelete(null);
            } else {
                handleCloseDialog();
            }
            setActionType(null);
        }
    }, [isAllAchievementsLoading, actionType]);

    const handleOpenDialog = (achievement = null) => {
        if (achievement) {
            setEditingAchievement(achievement);
            setFormData({
                name: achievement.name,
                description: achievement.description,
                locked_description: achievement.locked_description || '',
                achievement_type: achievement.achievement_type,
                group_id: achievement.group_id || '',
                max_actions_required: achievement.max_actions_required ?? '',
                blob_path: achievement.blob_path || '',
                bucket_name: achievement.bucket_name || '',
                due_time: achievement.due_time || '',
                team_id: achievement.team_id || '',
                user_id: achievement.user_id || '',
                is_hidden: achievement.is_hidden ?? true,
                is_shareable: achievement.is_shareable ?? true
            });
        } else {
            setEditingAchievement(null);
            setFormData({
                name: '',
                description: '',
                locked_description: '',
                achievement_type: 'Impact',
                group_id: '',
                max_actions_required: '',
                blob_path: '',
                bucket_name: '',
                due_time: '',
                team_id: '',
                user_id: '',
                is_hidden: true,
                is_shareable: true
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingAchievement(null);
        setSelectedFile(null);
        setFormData({
            name: '',
            description: '',
            locked_description: '',
            achievement_type: 'easy',
            group_id: '',
            max_actions_required: '',
            blob_path: '',
            bucket_name: '',
            due_time: '',
            team_id: '',
            user_id: '',
            is_hidden: true,
            is_shareable: true
        });
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                alert('Please select an image file');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                alert('File size must be less than 5MB');
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleUploadImage = () => {
        if (!selectedFile) {
            alert('Please select a file first');
            return;
        }
        const formData = new FormData();
        formData.append('image', selectedFile);
        uploadAchievementImage(formData);
    };

    const handleSubmit = () => {
        const achievementData = {
            name: formData.name,
            description: formData.description,
            locked_description: formData.locked_description || null,
            achievement_type: formData.achievement_type,
            group_id: formData.group_id || null,
            max_actions_required: formData.max_actions_required || null,
            blob_path: formData.blob_path || null,
            bucket_name: formData.bucket_name || null,
            due_time: formData.due_time || null,
            team_id: formData.team_id || null,
            user_id: formData.user_id || null,
            is_hidden: formData.is_hidden ?? true,
            is_shareable: formData.is_shareable ?? true
        };
        if (editingAchievement) {
            updateAchievement({
                ...achievementData,
                achievement_id: editingAchievement.achievement_id
            });
            setActionType('update');
        } else {
            postAchievement(achievementData);
            setActionType('create');
        }
    };

    const handleDeleteClick = (achievement) => {
        setAchievementToDelete(achievement);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (achievementToDelete) {
            deleteAchievement(achievementToDelete.achievement_id);
            setActionType('delete');
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setAchievementToDelete(null);
    };

    const getTeamName = (teamId) => {
        const team = teams.find(t => t.team_id === teamId);
        return team ? team.team_name : 'N/A';
    };

    const getUserName = (userId) => {
        const user = users.find(u => u.value === userId);
        return user ? `${user.first_name} ${user.last_name}` : 'N/A';
    };

    const handleIsGridViewChange = (event, newView) => {
        if (newView !== null) {
            setIsGridView(newView === 'grid');
            setAchievementsPerPage(newView === 'grid' ? 6 : 12);
        }
    }
    return (
        <Box>
            {/* Header with Create Button and Search */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Manage Achievements
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <IconButton
                        onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                        disabled={isAllAchievementsLoading}
                        size="small"
                        sx={{ border: '1px solid lightgray', borderRadius: '4px' }}
                    >
                        <Tooltip title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'} by type and name`}>
                            <Typography>
                                <IconComponent iconName={sortOrder === 'asc' ? 'chevron-up-circle-outline' : 'chevron-down-circle-outline'} style={{ fontSize: '20px' }} />
                            </Typography>
                        </Tooltip>
                    </IconButton>
                    <ToggleButtonGroup
                        value={isGridView ? 'grid' : 'list'}
                        exclusive
                        onChange={handleIsGridViewChange}
                        disabled={isAllAchievementsLoading}
                        size="medium"
                    >
                        <ToggleButton value="grid" aria-label="grid view">
                            <IconComponent iconName="grid" />
                        </ToggleButton>
                        <ToggleButton value="list" aria-label="list view">
                            <IconComponent iconName="list" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Button
                        variant="contained"
                        startIcon={<IconComponent iconName="add" />}
                        onClick={() => handleOpenDialog()}
                        disabled={isAllAchievementsLoading}
                        sx={{
                            background: 'linear-gradient(130deg, #1a242d 0%, #0f82b7 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #0b1014 0%, #0b597d 100%)',
                            }
                        }}
                    >
                        <Typography variant="button" sx={{ textTransform: 'none', fontWeight: 600 }}>
                            Create Achievement
                        </Typography>
                    </Button>
                </Box>
            </Box>

            {/* Search Bar */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={8}>
                    <TextField
                        fullWidth
                        placeholder="Search achievements by name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        disabled={isAllAchievementsLoading}
                        InputProps={{
                            startAdornment: (
                                <IconComponent iconName="search" style={{ marginRight: 8, color: 'text.secondary' }} />
                            ),
                            endAdornment: searchTerm && (
                                <IconButton
                                    size="small"
                                    onClick={() => setSearchTerm('')}
                                    sx={{ marginRight: -8 }}
                                >
                                    <IconComponent iconName="x" style={{ fontSize: 16 }} />
                                </IconButton>
                            )
                        }}
                        size="small"
                    />
                </Grid>
                <Grid item xs={4}>
                    <ChipSelect
                        options={achievementsTypes.map(type => ({ value: type.achievement_type, label: type.achievement_type, icon: type.icon, bgcolor: type.bgcolor }))}
                        selectedValues={selectedTypes}
                        onChange={setSelectedTypes}
                        label="Filter by Type"
                    />
                </Grid>
            </Grid>
            {searchTerm && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 1 }}>
                    Showing {filteredAchievements.length} of {allAchievements.length} achievements
                </Typography>
            )}
            {!searchTerm && allAchievements.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 1 }}>
                    Showing {currentAchievements.length} of {allAchievements.length} achievements (Page {currentPage} of {totalPages})
                </Typography>
            )}

            {/* Loading indicator */}
            {isAllAchievementsLoading && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                        Loading...
                    </Typography>
                </Box>
            )}

            {/* Achievements List */}
            {!isAllAchievementsLoading && (
                isGridView ? (
                    <Grid container spacing={2}>
                        {currentAchievements.map((achievement) => {
                            const difficultyColors = getAchievementColors(achievement.achievement_type);
                            return (
                                <Grid item xs={12} md={6} lg={4} key={achievement.achievement_id}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                        }}
                                    >
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                                                    {achievement.name}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenDialog(achievement)}
                                                        disabled={isAllAchievementsLoading}
                                                        sx={{ color: 'primary.main' }}
                                                    >
                                                        <IconComponent iconName="pencil" style={{ fontSize: 16 }} />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteClick(achievement)}
                                                        disabled={isAllAchievementsLoading}
                                                        sx={{ color: 'error.main' }}
                                                    >
                                                        <IconComponent iconName="trash" style={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Box>
                                            </Box>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ mb: 2, lineHeight: 1.5, textAlign: 'justify' }}
                                            >
                                                {achievement.description}
                                            </Typography>

                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                                                {achievement.max_actions_required && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Actions required: {achievement.max_actions_required} (Group ID #{achievement.group_id})
                                                    </Typography>
                                                )}
                                                {achievement.due_time && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Expires: {achievement.due_time}
                                                    </Typography>
                                                )}
                                                {achievement.team_id && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Team: {getTeamName(achievement.team_id)}
                                                    </Typography>
                                                )}
                                                {achievement.user_id && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        User: {getUserName(achievement.user_id)}
                                                    </Typography>
                                                )}
                                                {!achievement.team_id && !achievement.user_id && (
                                                    <Typography variant="caption" color="success.main">
                                                        Available to all users
                                                    </Typography>
                                                )}
                                                <Typography variant="caption" color={achievement.is_shareable ? 'success.main' : 'text.secondary'}>
                                                    {achievement.is_shareable ? '✓ Shareable' : '✗ Not shareable'}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Chip
                                                    label={achievement.achievement_type}
                                                    size="small"
                                                    sx={{
                                                        ...difficultyColors,
                                                        fontSize: '11px',
                                                        fontWeight: 600
                                                    }}
                                                />
                                                <Typography variant="caption" color="text.secondary">
                                                    Updated: {achievement.updated_at}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                ) : (
                    <>
                        {currentAchievements.map((achievement) => {
                            const difficultyColors = getAchievementColors(achievement.achievement_type);
                            return (
                                <Card
                                    key={achievement.achievement_id}
                                    sx={{
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        p: 1
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 2 }}>
                                            {achievement.name}
                                        </Typography>
                                        <Chip
                                            label={achievement.achievement_type}
                                            size="small"
                                            sx={{
                                                ...difficultyColors,
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                mr: 2
                                            }}
                                        />
                                        {achievement.is_shareable && (
                                            <Typography variant="caption" color="success.main" sx={{ mr: 2 }}>
                                                ✓ Shareable
                                            </Typography>
                                        )}
                                        {!achievement.team_id && !achievement.user_id && (
                                            <Typography variant="caption" color="success.main">
                                                Available to all users
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenDialog(achievement)}
                                            disabled={isAllAchievementsLoading}
                                            sx={{ color: 'primary.main' }}
                                        >
                                            <IconComponent iconName="pencil" style={{ fontSize: 16 }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeleteClick(achievement)}
                                            disabled={isAllAchievementsLoading}
                                            sx={{ color: 'error.main' }}
                                        >
                                            <IconComponent iconName="trash" style={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                </Card>
                            );
                        })}
                    </>
                )
            )}

            {/* Pagination */}
            {!isAllAchievementsLoading && totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, mb: 2 }}>
                    <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                        <InputLabel>Items per page</InputLabel>
                        <Select
                            value={achievementsPerPage}
                            label="Items per page"
                            onChange={handleAchievementsPerPageChange}
                        >
                            <MenuItem value={6}>6</MenuItem>
                            <MenuItem value={12}>12</MenuItem>
                            <MenuItem value={18}>18</MenuItem>
                            <MenuItem value={24}>24</MenuItem>
                        </Select>
                    </FormControl>
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        size="medium"
                        showFirstButton
                        showLastButton
                        sx={{
                            '& .MuiPaginationItem-root': {
                                borderRadius: '8px',
                                margin: '0 4px',
                            },
                            '& .Mui-selected': {
                                background: 'linear-gradient(135deg, #1a242d 0%, #0f82b7 100%)',
                                color: 'white',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #0b1014 0%, #0b597d 100%)',
                                }
                            }
                        }}
                    />
                </Box>
            )}

            {/* No achievements message */}
            {!isAllAchievementsLoading && filteredAchievements.length === 0 && !searchTerm && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <IconComponent iconName="trophy" style={{ fontSize: 48, color: 'gray', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        No achievements found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Create your first achievement to get started
                    </Typography>
                </Box>
            )}

            {/* Show message when no results */}
            {!isAllAchievementsLoading && filteredAchievements.length === 0 && searchTerm && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <IconComponent iconName="magnifying-glass" style={{ fontSize: 48, color: 'gray', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        No achievements found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Try adjusting your search terms
                    </Typography>
                </Box>
            )}

            {/* Create/Edit Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {editingAchievement ? 'Edit achievement' : 'Create a new achievement'}
                    <Divider />
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Achievement name"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                required
                                disabled={isAllAchievementsLoading}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description when unlocked"
                                value={formData.description}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                                multiline
                                rows={4}
                                required
                                disabled={isAllAchievementsLoading}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description while locked"
                                value={formData.locked_description}
                                onChange={(e) => handleInputChange('locked_description', e.target.value)}
                                multiline
                                rows={4}
                                disabled={isAllAchievementsLoading}
                            />  
                        </Grid>
                        <Grid item xs={4}>
                            <FormControl fullWidth>
                                <InputLabel>Achievement Type</InputLabel>
                                <Select
                                    value={formData.achievement_type}
                                    label="Achievement Type"
                                    onChange={(e) => handleInputChange('achievement_type', e.target.value)}
                                    disabled={isAllAchievementsLoading}
                                >
                                    {achievementsTypes.map(type => (
                                        <MenuItem key={type.achievement_type} value={type.achievement_type}>
                                            <Typography sx={{ color: type.bgcolor }}>
                                                {type.icon} {type.achievement_type}
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label="Group ID"
                                type="number"
                                value={formData.group_id}
                                onChange={(e) => handleInputChange('group_id', e.target.value)}
                                helperText="Optional: ID to group related achievements"
                                disabled={isAllAchievementsLoading}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label="Max Actions Required"
                                type="number"
                                value={formData.max_actions_required}
                                onChange={(e) => handleInputChange('max_actions_required', e.target.value)}
                                helperText="Leave empty if not applicable"
                                disabled={isAllAchievementsLoading}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 3, textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                                    {editingAchievement ? 'Upload New Image' : 'Upload Achievement Image'}
                                </Typography>

                                {formData.blob_path && (
                                    <Box sx={{ mb: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                                        <Typography variant="body2" color="success.main" sx={{ mb: 1, fontWeight: 600 }}>
                                            ✓ Image uploaded successfully
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            Main image: {formData.blob_path}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            Bucket: {formData.bucket_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            Thumbnail will be automatically generated with "_thumbnail" suffix
                                        </Typography>
                                    </Box>
                                )}

                                <input
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    id="image-upload"
                                    type="file"
                                    onChange={handleFileChange}
                                    disabled={isUploadingAchievementImage || isAllAchievementsLoading}
                                />
                                <label htmlFor="image-upload">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        disabled={isUploadingAchievementImage || isAllAchievementsLoading}
                                        startIcon={<IconComponent iconName="upload" />}
                                        sx={{ mb: 2 }}
                                    >
                                        {selectedFile ? selectedFile.name : 'Select Image File'}
                                    </Button>
                                </label>

                                {selectedFile && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            onClick={handleUploadImage}
                                            disabled={isUploadingAchievementImage || isAllAchievementsLoading}
                                            startIcon={isUploadingAchievementImage ? <IconComponent iconName="spinner" /> : <IconComponent iconName="cloud-upload" />}
                                        >
                                            {isUploadingAchievementImage ? 'Uploading...' : 'Upload Image'}
                                        </Button>
                                    </Box>
                                )}

                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                                    Supported formats: JPG, PNG, GIF. Max size: 5MB. Thumbnail will be generated automatically.
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label="Due Date"
                                type="date"
                                value={formData.due_time}
                                onChange={(e) => handleInputChange('due_time', e.target.value)}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                helperText="Leave empty if it does not expire"
                                disabled={isAllAchievementsLoading}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <FormControl fullWidth>
                                <InputLabel>Team</InputLabel>
                                <Select
                                    value={formData.team_id}
                                    label="Team"
                                    onChange={(e) => handleInputChange('team_id', e.target.value)}
                                    disabled={isAllAchievementsLoading}
                                >
                                    <MenuItem value="">None (Available to all)</MenuItem>
                                    {teams.map(team => (
                                        <MenuItem key={team.team_id} value={team.team_id}>
                                            {team.team_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={4}>
                            <FormControl fullWidth>
                                <InputLabel>User</InputLabel>
                                <Select
                                    value={formData.user_id}
                                    label="User"
                                    onChange={(e) => handleInputChange('user_id', e.target.value)}
                                    disabled={isAllAchievementsLoading}
                                >
                                    <MenuItem value="">None (Available to all)</MenuItem>
                                    {users.map(user => (
                                        <MenuItem key={user.value} value={user.value}>
                                            {user.first_name} {user.last_name} ({user.email})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_hidden}
                                        onChange={(e) => handleInputChange('is_hidden', e.target.checked)}
                                        disabled={isAllAchievementsLoading}
                                    />
                                }
                                label="Hidden Achievement"
                                sx={{
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '0.875rem'
                                    }
                                }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                                Whether it is visible to users before they are unlocked
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_shareable}
                                        onChange={(e) => handleInputChange('is_shareable', e.target.checked)}
                                        disabled={isAllAchievementsLoading}
                                    />
                                }
                                label="Shareable Achievement"
                                sx={{
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '0.875rem'
                                    }
                                }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                                Whether the achievement is shared on the achievements timeline
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            {!formData.team_id && !formData.user_id && (
                                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                                    Any user can acquire this achievement
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={isAllAchievementsLoading}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={!formData.name || !formData.description || isAllAchievementsLoading || isUploadingAchievementImage}
                    >
                        {isAllAchievementsLoading ? 'Saving...' : (editingAchievement ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconComponent iconName="trash" style={{ color: '#d32f2f', fontSize: 20 }} />
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to delete this achievement?
                    </Typography>
                    {achievementToDelete && (
                        <Box sx={{
                            p: 2,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.300'
                        }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                {achievementToDelete.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'justify' }}>
                                {achievementToDelete.description}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Chip
                                    label={achievementToDelete.achievement_type}
                                    size="small"
                                    sx={{
                                        ...getAchievementColors(achievementToDelete.achievement_type),
                                        fontSize: '10px',
                                        fontWeight: 600
                                    }}
                                />
                            </Box>
                            <Box sx={{ mt: 1 }}>
                                {achievementToDelete.max_actions_required && (
                                    <Typography variant="caption" color="text.secondary">
                                        Actions: {achievementToDelete.max_actions_required} (Group ID #{achievementToDelete.group_id})
                                    </Typography>
                                )}
                                {achievementToDelete.due_time && (
                                    <Typography variant="caption" color="text.secondary">
                                        Expires: {achievementToDelete.due_time}
                                    </Typography>
                                )}
                                {achievementToDelete.team_id && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        Team: {getTeamName(achievementToDelete.team_id)}
                                    </Typography>
                                )}
                                {achievementToDelete.user_id && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        User: {getUserName(achievementToDelete.user_id)}
                                    </Typography>
                                )}
                                {!achievementToDelete.team_id && !achievementToDelete.user_id && (
                                    <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                                        Available to all users
                                    </Typography>
                                )}
                                <Typography variant="caption" color={achievementToDelete.is_shareable ? 'success.main' : 'text.secondary'} sx={{ display: 'block' }}>
                                    {achievementToDelete.is_shareable ? '✓ Shareable' : '✗ Not shareable'}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 600 }}>
                        ⚠️ This action cannot be undone. All progress associated with this achievement will be lost.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={isAllAchievementsLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        color="error"
                        disabled={isAllAchievementsLoading}
                        startIcon={<IconComponent iconName="trash" />}
                    >
                        {isAllAchievementsLoading ? 'Deleting...' : 'Delete Achievement'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AchievementManagement;
